import React, { useEffect, useRef } from 'react';
import { TableCell, TableRow, Badge, Checkbox } from '@roger/ui-components';
import {
  Loader2,
  User,
  Calendar,
  Building,
  FileText,
  XCircle,
  RefreshCw,
  Stethoscope,
  MapPin,
  UserPlus,
  Pencil,
} from 'lucide-react';
import { useState } from 'react';
import { IntakeUserAssignmentDialog } from './IntakeUserAssignmentDialog';
import type { IntakeRecord } from '../store/slices/intakeSlice';
import type { AgencyClinicResponse } from '../services/api/types.gen';
import { useWorkflowStatus } from '../hooks/useWorkflowStatus';
import StepStatusIndicator from './StepStatusIndicator';
import { useStore } from '../store';
import type { EnabledTabs } from '../constants/intakeTabs';
import {
  formatIntakeSocRocCell,
  getIntakeManagerColumnDisplayString,
  getIntakeUploadFilename,
} from '../utils/intakeManagerColumnDisplay';
import { formatDateToZeroPaddedMMDDYYYY } from '../utils/dateUtils';
import { useIntakeFormSchema } from '../config/intakeFormSchema';
import OverflowTooltipText from './OverflowTooltipText';
import { NON_ADMIT_REASON_LABELS } from '../constants/nonAdmitReasons';
import { ReportLink } from './reporting/ReportLink';
import { patientChartPath } from '../utils/navigation';

interface IntakeRowWithStatusProps {
  intake: IntakeRecord;
  isProcessing: boolean;
  getPatientName: (intake: IntakeRecord) => string;
  agencyClinics: AgencyClinicResponse[];
  agencyName?: string;
  onClick: (e: React.MouseEvent) => void;
  onWorkflowComplete?: (intakeId: string) => void;
  enabledTabs?: EnabledTabs;
  onRerun?: (intake: IntakeRecord) => void;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  onAssignComplete?: () => void;
  /** Ordered Intake Manager column ids (checkbox is always separate). */
  visibleColumns: string[];
}

const IntakeRowWithStatus: React.FC<IntakeRowWithStatusProps> = ({
  intake,
  isProcessing,
  getPatientName,
  agencyClinics,
  agencyName,
  onClick,
  onWorkflowComplete,
  enabledTabs,
  onRerun,
  showCheckbox = false,
  isSelected = false,
  onCheckboxChange,
  onAssignComplete,
  visibleColumns,
}) => {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const getClinicianNameById = useStore(state => state.getClinicianNameById);
  const { schema: intakeFormSchema } = useIntakeFormSchema();

  const workflowStatus = useWorkflowStatus({
    intakeId: intake.id,
    enabled: isProcessing,
  });

  const isFailed =
    (isProcessing && workflowStatus.isFailed) ||
    (intake.processing_status === 'failed' && !!intake.processing_errors);

  const showProcessingUI = isProcessing || isFailed;

  // A reason this build does not know about (rolling deploy, server ahead)
  // falls back to the bare chip rather than rendering the literal "undefined".
  const nonAdmitReasonLabel = intake.non_admit_reason
    ? (NON_ADMIT_REASON_LABELS[intake.non_admit_reason] ?? '')
    : '';

  const hasCalledCompleteRef = useRef(false);

  useEffect(() => {
    if (
      (workflowStatus.isCompleted || workflowStatus.isFailed) &&
      onWorkflowComplete &&
      !hasCalledCompleteRef.current
    ) {
      console.log(
        '[IntakeRowWithStatus] Workflow finished for intake:',
        intake.id,
        'completed:',
        workflowStatus.isCompleted,
        'failed:',
        workflowStatus.isFailed
      );
      hasCalledCompleteRef.current = true;
      onWorkflowComplete(intake.id);
    }
  }, [
    workflowStatus.isCompleted,
    workflowStatus.isFailed,
    onWorkflowComplete,
    intake.id,
  ]);

  const resolveClinicDisplay = () => {
    const clinic = agencyClinics.find(c => c.id === intake.agency_clinic_id);
    if (clinic?.name) return clinic.name;
    if (agencyClinics.length === 1 && !agencyClinics[0].name && agencyName) {
      return agencyName;
    }
    return 'N/A';
  };

  const renderColumn = (columnId: string): React.ReactNode => {
    switch (columnId) {
      case 'patient': {
        const uploadFilename = getIntakeUploadFilename(intake);
        return (
          <TableCell key={columnId}>
            <div className='flex items-center gap-2'>
              {showProcessingUI ? (
                <FileText className='h-4 w-4 shrink-0 text-muted-foreground' />
              ) : (
                <User className='h-4 w-4 text-muted-foreground' />
              )}
              <div className='min-w-0'>
                <div className='font-medium'>
                  {showProcessingUI ? (
                    <div className='flex items-center gap-2'>
                      {isFailed ? (
                        <XCircle className='h-3 w-3 shrink-0 text-red-600' />
                      ) : (
                        <Loader2 className='h-3 w-3 shrink-0 animate-spin' />
                      )}
                      <OverflowTooltipText
                        text={uploadFilename ?? 'Processing...'}
                        className='max-w-[200px]'
                      />
                    </div>
                  ) : intake.patient_id ? (
                    <ReportLink to={patientChartPath(intake.patient_id)}>
                      {getPatientName(intake)}
                    </ReportLink>
                  ) : (
                    getPatientName(intake)
                  )}
                </div>
                {intake.patient_dob && !showProcessingUI && (
                  <div className='text-muted-foreground'>
                    DOB: {formatDateToZeroPaddedMMDDYYYY(intake.patient_dob)}
                  </div>
                )}
              </div>
            </div>
          </TableCell>
        );
      }
      case 'patient_dob':
        return (
          <TableCell key={columnId}>
            {showProcessingUI ? null : (
              <div className='font-medium'>
                {formatDateToZeroPaddedMMDDYYYY(intake.patient_dob) || 'N/A'}
              </div>
            )}
          </TableCell>
        );
      case 'status':
        return (
          <TableCell key={columnId}>
            {showProcessingUI ? (
              <div className='flex items-center gap-2'>
                {isFailed ? (
                  <>
                    <XCircle className='h-3 w-3 text-red-600' />
                    <Badge
                      variant='destructive'
                      className='flex items-center gap-1'
                    >
                      <FileText className='h-3 w-3' />
                      Failed
                    </Badge>
                  </>
                ) : (
                  <>
                    <Loader2 className='h-3 w-3 animate-spin' />
                    <Badge
                      variant='secondary'
                      className='flex items-center gap-1'
                    >
                      <FileText className='h-3 w-3' />
                      Processing...
                    </Badge>
                  </>
                )}
              </div>
            ) : intake.non_admit || intake.archived ? (
              <div className='flex items-center gap-2 flex-wrap'>
                {intake.non_admit && (
                  <div className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                    <div className='w-2 h-2 rounded-full bg-gray-400' />
                    {/* The reason rides the chip, because the
                        default list filter hides non-admit rows and a
                        dedicated column would be blank for everyone. The
                        free text stays in the title so the cell cannot
                        grow. */}
                    <span
                      className='whitespace-nowrap'
                      title={intake.non_admit_reason_details ?? undefined}
                    >
                      {nonAdmitReasonLabel
                        ? `Non-Admit · ${nonAdmitReasonLabel}`
                        : 'Non-Admit'}
                    </span>
                  </div>
                )}
                {intake.archived && (
                  <div className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                    <div className='w-2 h-2 rounded-full bg-orange-500' />
                    <span>Archived</span>
                  </div>
                )}
              </div>
            ) : (
              <StepStatusIndicator
                tabStatuses={intake.tab_statuses}
                enabledTabs={enabledTabs}
              />
            )}
          </TableCell>
        );
      case 'clinic':
        return (
          <TableCell key={columnId}>
            {showProcessingUI ? null : (
              <div className='flex items-center gap-2'>
                <Building className='h-4 w-4 text-muted-foreground' />
                <div className='font-medium'>{resolveClinicDisplay()}</div>
              </div>
            )}
          </TableCell>
        );
      case 'insurance':
        return (
          <TableCell key={columnId}>
            {showProcessingUI ? null : (
              <div className='flex items-center gap-2'>
                <Building className='h-4 w-4 text-muted-foreground' />
                <div>
                  <div className='font-medium'>
                    {intake.agency_insurance?.name || 'N/A'}
                  </div>
                  {intake.primary_member_id && (
                    <div className='text-muted-foreground'>
                      ID: {intake.primary_member_id}
                    </div>
                  )}
                </div>
              </div>
            )}
          </TableCell>
        );
      case 'referring_provider':
        return (
          <TableCell key={columnId}>
            {showProcessingUI ? null : (
              <div className='flex items-center gap-2'>
                <Stethoscope className='h-4 w-4 text-muted-foreground' />
                <div className='font-medium'>
                  {intake.referring_physician || 'N/A'}
                </div>
              </div>
            )}
          </TableCell>
        );
      case 'referral_source':
        return (
          <TableCell key={columnId}>
            {showProcessingUI ? null : (
              <div className='flex items-center gap-2'>
                <MapPin className='h-4 w-4 text-muted-foreground' />
                <div className='font-medium'>
                  {intake.referral_source || 'N/A'}
                </div>
              </div>
            )}
          </TableCell>
        );
      case 'assignee':
        return (
          <TableCell key={columnId}>
            <div
              className='font-medium cursor-pointer group flex items-center gap-2 hover:text-primary transition-colors'
              onClick={e => {
                e.stopPropagation();
                setAssignDialogOpen(true);
              }}
            >
              {intake.assigned_to_user ? (
                <>
                  <OverflowTooltipText
                    text={intake.assigned_to_user.email}
                    className='max-w-[150px]'
                  />
                  <Pencil className='h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground' />
                </>
              ) : (
                <span className='text-muted-foreground flex items-center gap-1 group-hover:text-primary'>
                  Assign <UserPlus className='h-3 w-3' />
                </span>
              )}
              <div onClick={e => e.stopPropagation()} className='hidden'>
                <IntakeUserAssignmentDialog
                  open={assignDialogOpen}
                  onOpenChange={setAssignDialogOpen}
                  intakes={[intake]}
                  onSuccess={onAssignComplete}
                />
              </div>
            </div>
          </TableCell>
        );
      case 'soc_date':
        return (
          <TableCell key={columnId}>
            {showProcessingUI ? null : (
              <div>
                <div className='font-medium'>
                  {formatIntakeSocRocCell(intake) || 'N/A'}
                </div>
                {intake.kinnser_clinician_id && (
                  <div className='text-muted-foreground'>
                    {getClinicianNameById(intake.kinnser_clinician_id) ||
                      intake.kinnser_clinician_id}
                  </div>
                )}
              </div>
            )}
          </TableCell>
        );
      case 'created':
        return (
          <TableCell key={columnId}>
            <div className='flex items-center gap-2'>
              <Calendar className='h-4 w-4 text-muted-foreground' />
              <span>{formatDateToZeroPaddedMMDDYYYY(intake.created_at)}</span>
              {isFailed && (
                <div className='ml-2 flex items-center gap-1 text-xs text-red-600'>
                  {onRerun ? (
                    <span
                      onClick={e => {
                        e.stopPropagation();
                        onRerun(intake);
                      }}
                      className='font-semibold flex items-center gap-1 text-red-600 cursor-pointer hover:text-red-700'
                    >
                      Rerun
                      <RefreshCw className='h-3 w-3' />
                    </span>
                  ) : (
                    <>
                      <XCircle className='h-3 w-3' />
                      <span>Failed</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </TableCell>
        );
      default: {
        const text = getIntakeManagerColumnDisplayString(
          intake,
          columnId,
          intakeFormSchema ?? undefined
        );
        return (
          <TableCell key={columnId}>
            {showProcessingUI ? null : (
              <OverflowTooltipText
                text={text || 'N/A'}
                className='font-medium max-w-[220px]'
              />
            )}
          </TableCell>
        );
      }
    }
  };

  return (
    <TableRow
      key={intake.id}
      className={
        showProcessingUI
          ? isFailed
            ? 'bg-red-50/50 cursor-pointer opacity-60'
            : 'bg-blue-50/50 cursor-not-allowed opacity-60'
          : 'hover:bg-muted/50 cursor-pointer'
      }
      onClick={onClick}
    >
      {showCheckbox && (
        <TableCell onClick={e => e.stopPropagation()}>
          <Checkbox checked={isSelected} onCheckedChange={onCheckboxChange} />
        </TableCell>
      )}
      {visibleColumns.map(colId => renderColumn(colId))}
    </TableRow>
  );
};

export default IntakeRowWithStatus;
